import { Injectable } from '@angular/core';
import { TourStep } from './tour.service';

@Injectable({
  providedIn: 'root'
})
export class TourConfigService {

  constructor() { }

  getDashboardTourSteps(): TourStep[] {
    return [
      {
        id: 'statistics-overview',
        title: 'Statistics Overview',
        text: 'Daily data ingestion from Congress and White House activities. New information is processed and updated every day to keep you informed with latest legislative and executive activities.',
        element: '#stats-boxes',
        position: 'bottom'
      },
      {
        id: 'documents-types',
        title: 'Documents Types',
        text: 'Top 3 count of article types that have been ingested. Shows the most common document categories from the daily data collection.',
        element: '#doc-types-stats',
        position: 'bottom'
      },
      {
        id: 'relevant-keywords',
        title: 'Relevant Keywords',
        text: 'Chart displaying article totals by relevance keywords. Visualizes the most frequently mentioned terms across all ingested documents.',
        element: '#relevant-keys-stats',
        position: 'bottom'
      },
      {
        id: 'news-feed',
        title: 'News Feed',
        text: 'Main content hub displaying all daily articles with search and filtering capabilities. Browse, search, and interact with Congress and White House documents from today\'s data sync.',
        element: '#news-feed',
        position: 'left'
      },
      {
        id: 'search-feature',
        title: 'Search Feature',
        text: 'Search through any data within articles. Find specific content, keywords, or information across all documents in the feed.',
        element: 'input[placeholder="Search..."]',
        position: 'bottom'
      },
      {
        id: 'document-type-filter',
        title: 'Document Type Filter',
        text: 'Filter articles by document type to focus on specific categories like bills, resolutions, or executive orders.',
        element: '#news-feed button',
        position: 'bottom'
      },
      {
        id: 'pin-feature',
        title: 'Pin Feature',
        text: 'Use this pin to create boards where you can organize your content. You can create different boards for different projects or topics.',
        element: '#news-feed app-pin-manager-button:first-child',
        position: 'right'
      },
      {
        id: 'boards-link',
        title: 'Boards Management',
        text: 'Access your boards to organize and manage your saved articles. Create different boards for different projects or research topics.',
        element: '#sidebar-boards-link',
        position: 'right'
      },
      {
        id: 'congress-activities',
        title: 'Congress Calendar',
        text: 'View today\'s schedule and upcoming congress activities. Stay organized with your daily agenda.',
        element: '#today-schedule',
        position: 'bottom'
      },
      {
        id: 'congress-history',
        title: 'Congress History',
        text: 'Access previous months and view all congress activities organized by date. Browse through historical data and past events.',
        element: '#sidebar-calendar-link',
        position: 'right'
      }
    ];
  }

  getBoardsTourSteps(): TourStep[] {
    return [
      {
        id: 'draft-column',
        title: 'Draft Column',
        text: 'This is where your new boards start. Boards in the Draft stage are still being created and refined. You can add team members here while the board remains private - perfect for organizing your team before making the board public.',
        element: '#draft-column',
        position: 'bottom'
      },
      {
        id: 'in-review-column',
        title: 'In Review Column',
        text: 'Boards in this phase are accessible only to board members. Here, all team members are required to provide feedback - positive or negative - ensuring everyone is aware of the content and decisions included in the board.',
        element: '#in-review-column',
        position: 'bottom'
      },
      {
        id: 'completed-column',
        title: 'Completed Column',
        text: 'Finished boards are moved here after all member feedback has been processed. This column contains your finalized research boards with government articles and activities - ready for reference, sharing, or archiving.',
        element: '#completed-column',
        position: 'bottom'
      },
      {
        id: 'board-filter',
        title: 'Filter & Search',
        text: 'Use these filters to find specific boards. Search by name, filter by team members, relevant keys, or tags.',
        element: '#board-filter',
        position: 'bottom'
      }
    ];
  }

  getWorkAreaDraftTourSteps(): TourStep[] {
    return [
      {
        id: 'board-title-section',
        title: 'Board Title',
        text: 'This is where you can edit your board title. You can write a custom title or use AI to suggest a better one based on your board content.',
        element: 'input[placeholder="Enter board title..."]',
        position: 'bottom'
      },
      {
        id: 'title-refine-button',
        title: 'AI Title Refinement',
        text: 'Use AI to analyze your board articles and suggest a better title. The AI will review all your content and provide a more descriptive and accurate title suggestion.',
        element: '#refine-title-button',
        position: 'right'
      },
      {
        id: 'stage-selector',
        title: 'Board Stage Control',
        text: 'As board owner, you can change the board stage from Draft to In Review. Once in review, no more changes can be made and the board waits for feedback from invited members.',
        element: '#stage-selector',
        position: 'bottom'
      },
      {
        id: 'board-stats',
        title: 'Board Statistics',
        text: 'These are the statistics of everything you have in your board: Articles (federal records), Activities (congress events), Members (team), and Notes (your observations).',
        element: '.grid.grid-cols-2.md\\:grid-cols-4.gap-4',
        position: 'bottom'
      },
      {
        id: 'content-tabs',
        title: 'Content Organization Tabs',
        text: 'Organize your board content into different sections: Federal Records (articles), Congress Activities, Notes, and Q&A. Each tab contains specific types of content.',
        element: '.flex.flex-row.gap-1\\.5.flex-nowrap.overflow-x-auto',
        position: 'bottom'
      },
      {
        id: 'federal-records-tab',
        title: 'Federal Records',
        text: 'This tab contains all the articles and federal records that have been added to your board. You can review and manage the content here.',
        element: '#tab-articles',
        position: 'bottom'
      },
      {
        id: 'congress-activities-tab',
        title: 'Congress Activities',
        text: 'This tab shows scheduled congressional activities related to your board, including hearings, votes, and other legislative events.',
        element: '#tab-activities',
        position: 'bottom'
      },
      {
        id: 'qa-tab',
        title: 'Q&A with AI',
        text: 'Using AI, you can ask questions about the articles in your board and get intelligent answers. These Q&A sessions can be saved as notes in the board.',
        element: '#tab-qna',
        position: 'bottom'
      },
      {
        id: 'notes-tab',
        title: 'Notes',
        text: 'These are saved notes generated by AI from your Q&A sessions and other important information you want to keep track of.',
        element: '#tab-notes',
        position: 'bottom'
      },
      {
        id: 'add-article-button',
        title: 'Add New Articles',
        text: 'Add new federal records to your board. Search through available articles and pin them to your board for organization.',
        element: '#add-article-button',
        position: 'right'
      },
      {
        id: 'articles-list',
        title: 'Federal Records List',
        text: 'View all articles pinned to this board. Each article can be removed or managed individually. Articles are the core content of your research board.',
        element: '#articles-list',
        position: 'left'
      },

      {
        id: 'board-members',
        title: 'Team Members',
        text: 'Manage your board members and their access. As board owner, you can add team members, control permissions, and track member status (confirmed/rejected).',
        element: '#board-members',
        position: 'right'
      },
      {
        id: 'tags-manager',
        title: 'Tags Organization',
        text: 'Organize your board with tags. Create custom tags to categorize and filter your content effectively.',
        element: '#tags-manager',
        position: 'right'
      },
      {
        id: 'team-collaboration',
        title: 'Team Collaboration',
        text: 'Collaborate with your team through the chat panel. Share messages, discuss content, and coordinate on board activities with your invited members.',
        element: '#chat-panel-container',
        position: 'left'
      }
    ];
  }

  getWorkAreaMemberTourSteps(): TourStep[] {
    return [
      {
        id: 'board-title-readonly',
        title: 'Board Title',
        text: 'This is the board title. As a member, you can view it but cannot make changes since the board is in review stage.',
        element: 'input[placeholder="Enter board title..."]',
        position: 'bottom'
      },
      {
        id: 'board-stats-member',
        title: 'Board Statistics',
        text: 'View the current statistics of the board: Articles, Activities, Members, and Notes. These show the content that has been prepared for review.',
        element: '.grid.grid-cols-2.md\\:grid-cols-4.gap-4',
        position: 'bottom'
      },
      {
        id: 'board-members-member',
        title: 'Team Members',
        text: 'View all team members and their status. You can see who has confirmed or rejected the board content.',
        element: '#board-members',
        position: 'right'
      },
      {
        id: 'member-feedback',
        title: 'Provide Your Feedback',
        text: 'As a member, you must provide your feedback on the board content. Use the chat panel or contact the board admin to share your thoughts and suggestions.',
        element: '#chat-panel-container',
        position: 'left'
      }
    ];
  }

  getCongressCalendarTourSteps(): TourStep[] {
    return [
      {
        id: 'search-input',
        title: 'Search Congress Activity',
        text: 'Search through all congress activities and events. Use this search bar to find specific activities, keywords, or information across all congressional events.',
        element: '#search-input',
        position: 'bottom'
      },
      {
        id: 'types-selector',
        title: 'Event Type Filter',
        text: 'Filter congress activities by type. Select specific event types like hearings, votes, meetings, or other congressional activities to focus on particular categories.',
        element: '#types-selector',
        position: 'bottom'
      },
      {
        id: 'calendar-container',
        title: 'Congress Calendar View',
        text: 'Interactive calendar showing all congress activities organized by month. Click on any day to view detailed activities for that date. Days with events are highlighted.',
        element: '#calendar-container',
        position: 'top'
      },
      {
        id: 'month-activity-view',
        title: 'Daily Activity Details',
        text: 'Detailed view of congress activities for the selected day. Shows comprehensive information about hearings, votes, meetings, and other congressional events.',
        element: '#month-activity-view',
        position: 'left'
      }
    ];
  }
}
